import { ReactElement, useMemo } from 'react'
import { useSceneLineMarker } from '@editorHooks'

interface Props {
  wallDimensions?: BuildingDimensionsDataWithStart
  roofDimensions?: BuildingDimensionsDataWithStart
  qZoneDimensions?: BuildingDimensionsDataWithStart
}

const hasRenderableDimensions = (
  dimensions?: BuildingDimensionsDataWithStart,
): dimensions is BuildingDimensionsDataWithStart => {
  if (!dimensions || !dimensions.start) return false

  return (
    typeof dimensions.start.x === 'number' &&
    typeof dimensions.start.y === 'number' &&
    typeof dimensions.start.z === 'number' &&
    typeof dimensions.depth === 'number' &&
    typeof dimensions.width === 'number' &&
    typeof dimensions.height === 'number' &&
    dimensions.depth > 0 &&
    dimensions.width > 0 &&
    dimensions.height > 0
  )
}

const toMarkerLines = (dimensions?: BuildingDimensionsDataWithStart) => {
  if (!hasRenderableDimensions(dimensions)) return []

  const start = {
    x: dimensions.start.x,
    y: dimensions.start.y,
    z: dimensions.start.z,
  }
  const bottomRight = {
    x: start.x + dimensions.depth,
    y: start.y,
    z: start.z,
  }
  const topRight = {
    x: start.x + dimensions.depth,
    y: start.y + dimensions.width,
    z: start.z,
  }
  const topLeft = {
    x: start.x,
    y: start.y + dimensions.width,
    z: start.z,
  }
  const topHeight = {
    x: start.x,
    y: start.y,
    z: start.z + dimensions.height,
  }

  return [
    [start, bottomRight],
    [bottomRight, topRight],
    [topRight, topLeft],
    [topLeft, start],
    [start, topHeight],
  ]
}

const BuildingDimensionsSceneMarkers = ({
  wallDimensions,
  roofDimensions,
  qZoneDimensions,
}: Props): ReactElement => {
  const wallLines = useMemo(() => toMarkerLines(wallDimensions), [wallDimensions])
  const roofLines = useMemo(() => toMarkerLines(roofDimensions), [roofDimensions])
  const qZoneLines = useMemo(() => toMarkerLines(qZoneDimensions), [qZoneDimensions])

  useSceneLineMarker({
    lines: wallLines,
    enabled: wallLines.length > 0,
    color: '#105ba4',
    lineWidth: 4,
    renderOrder: 1000,
    disableDepthTest: true,
  })

  useSceneLineMarker({
    lines: roofLines,
    enabled: roofLines.length > 0,
    color: '#e32f27',
    lineWidth: 4,
    renderOrder: 1000,
    disableDepthTest: true,
  })

  useSceneLineMarker({
    lines: qZoneLines,
    enabled: qZoneLines.length > 0,
    color: 'black',
    lineWidth: 4,
    dashed: true,
    dashSize: 0.5,
    gapSize: 1,
    renderOrder: 1000,
    disableDepthTest: true,
  })

  return <></>
}

export default BuildingDimensionsSceneMarkers
